import React, { useState } from 'react';
import { updateUserLotConfig, updateUserCapitalConfig, updateUserSellerConfig, updateUserLimitsConfig, updateUserFeedConfig } from '../services/apiService';
import '../styles/RiskSettingsPanel.css';

// Daily limits the admin can override: user-record field, system-status key, label, whole number?
// The fallback defaults mirror application.properties for when system-status hasn't loaded yet.
const DAILY_LIMITS = [
  { field: 'limitLossStreak',      key: 'lossStreak',      label: 'Stop after losses in a row (all indices)', int: true,  fallback: 2 },
  { field: 'limitTotalLosses',     key: 'totalLosses',     label: 'Stop after losses in total (all indices)', int: true,  fallback: 0 },
  { field: 'limitIndexLockLosses', key: 'indexLockLosses', label: 'Lock an index after losses on it',         int: true,  fallback: 2 },
  { field: 'limitDailyStopR',      key: 'dailyStopR',      label: 'Stop when the day is down (R)',            int: false, fallback: 2.0 },
  { field: 'limitMaxTrades',       key: 'maxTrades',       label: 'Max SYSTEM trades per day',                int: true,  fallback: 8 },
  { field: 'limitProfitLockPct',   key: 'profitLockPct',   label: 'Profit lock: day goal (% of capital)',     int: false, fallback: 1.0 },
  { field: 'limitProfitLockGivebackPct', key: 'profitLockGivebackPct', label: 'Profit lock: stop after giving back (% of capital)', int: false, fallback: 0.4 },
];

const PROFIT_LOCK_MODES = [
  { value: 'GIVEBACK',  label: 'Half size at the goal, stop if the day gives back' },
  { value: 'HALF_SIZE', label: 'Half size at the goal, never stop' },
  { value: 'STOP',      label: 'Stop new entries at the goal' },
];

// sellerEnabledNow: the backend engine's current range-day selling state (system-status) —
// shown when the admin hasn't set the switch yet, i.e. the server's seller.enabled default.
// sellerSwitchLocked: the server ignores this switch (seller.admin-switch=false) — shown read-only.
// dailyLimitsNow: system-status dailyLimits — the limits in force and their server defaults.
// sizingNow: system-status sizing — the capital SYSTEM sizing uses now, its source and the size cuts.
const inr = (v) => `₹${Math.round(v).toLocaleString('en-IN')}`;

const RiskSettingsPanel = ({ currentUser, sellerEnabledNow, sellerSwitchLocked, dailyLimitsNow, sizingNow, onClose, onSaved }) => {
  const isAdmin = currentUser?.role === 'ADMIN';
  const [onRiskLots, setOnRiskLots]       = useState(String(currentUser?.onRiskLots ?? 1));
  const [takeTradeLots, setTakeTradeLots] = useState(String(currentUser?.takeTradeLots ?? 2));
  // Fixed-risk sizing (admin-only) is the backend default — empty accountCapital means "use the
  // server's sizing.capital-rs". See TradingSignalService.computeCapitalAwareLots on the backend
  // for how these three feed into actual lot sizing.
  const [capitalEnabled, setCapitalEnabled] = useState(currentUser?.accountCapitalRs != null);
  const [accountCapital, setAccountCapital] = useState(
    currentUser?.accountCapitalRs != null ? String(currentUser.accountCapitalRs) : '');
  const [onRiskRiskPct, setOnRiskRiskPct]       = useState(String(currentUser?.onRiskRiskPct ?? 0.5));
  const [takeTradeRiskPct, setTakeTradeRiskPct] = useState(String(currentUser?.takeTradeRiskPct ?? 1.0));
  // The capital the form would size with: the typed figure when ticked, else the server default.
  // FIXED_LOTS (sizing.capital-rs=0 and no own capital) is the only mode where the lot counts size SYSTEM trades.
  const serverCapital = sizingNow?.serverDefaultCapitalRs ?? null;
  const formCapital = capitalEnabled ? parseFloat(accountCapital) : serverCapital;
  const riskSizing = sizingNow ? sizingNow.mode === 'RISK' : true;
  const formRiskSizing = capitalEnabled ? formCapital > 0 : serverCapital == null ? riskSizing : serverCapital > 0;
  const initialSeller = sellerSwitchLocked ? sellerEnabledNow === true
    : currentUser?.sellerEnabled ?? sellerEnabledNow ?? true;
  const [sellerEnabled, setSellerEnabled] = useState(initialSeller);
  const initialPauseWithoutKite = currentUser?.pauseWithoutKite === true;
  const [pauseWithoutKite, setPauseWithoutKite] = useState(initialPauseWithoutKite);
  const limitDefault = (l) => dailyLimitsNow?.defaults?.[l.key] ?? l.fallback;
  // Each field starts at the admin's override, else the server default — always a real number.
  const [limits, setLimits] = useState(() => Object.fromEntries(DAILY_LIMITS.map(l =>
    [l.key, String(currentUser?.[l.field] ?? limitDefault(l))])));
  const profitLockModeDefault = dailyLimitsNow?.defaults?.profitLockMode ?? 'GIVEBACK';
  const [profitLockMode, setProfitLockMode] = useState(currentUser?.limitProfitLockMode ?? profitLockModeDefault);
  const limitsAtDefault = DAILY_LIMITS.every(l => Number(limits[l.key]) === limitDefault(l))
    && profitLockMode === profitLockModeDefault;
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving]   = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    const onRisk = parseInt(onRiskLots, 10);
    const takeTrade = parseInt(takeTradeLots, 10);
    if (!Number.isInteger(onRisk) || onRisk < 1 || onRisk > 50
        || !Number.isInteger(takeTrade) || takeTrade < 1 || takeTrade > 50) {
      setError('Lot counts must be whole numbers between 1 and 50.');
      return;
    }

    let capital = null;
    let onRiskPct = null;
    let takeTradePct = null;
    if (isAdmin) {
      onRiskPct = parseFloat(onRiskRiskPct);
      takeTradePct = parseFloat(takeTradeRiskPct);
      if (!(onRiskPct > 0) || onRiskPct > 10 || !(takeTradePct > 0) || takeTradePct > 10) {
        setError('Risk % per trade must be between 0 and 10.');
        return;
      }
      if (capitalEnabled) {
        capital = parseFloat(accountCapital);
        if (!(capital > 0)) {
          setError('Account capital must be a positive number, or untick it to use the server default.');
          return;
        }
      }
    }

    // Daily limits: a value equal to the server default is sent as null so it keeps following
    // the default if application.properties changes later.
    let limitsBody = null;
    if (isAdmin) {
      limitsBody = {};
      for (const l of DAILY_LIMITS) {
        const v = Number(limits[l.key]);
        const valid = limits[l.key] !== '' && Number.isFinite(v) && v >= 0
          && (l.int ? Number.isInteger(v) && v <= 50 : v <= 20);
        if (!valid) {
          setError(`${l.label}: ${l.int ? 'a whole number from 0 to 50' : 'a number from 0 to 20'} (0 = off).`);
          return;
        }
        limitsBody[l.key] = v === limitDefault(l) ? null : v;
      }
      limitsBody.profitLockMode = profitLockMode === profitLockModeDefault ? null : profitLockMode;
    }

    setSaving(true);
    try {
      let updated = await updateUserLotConfig(currentUser.username, currentUser.username, onRisk, takeTrade);
      if (isAdmin) {
        updated = await updateUserCapitalConfig(
          currentUser.username, currentUser.username, capital, onRiskPct, takeTradePct);
        if (!sellerSwitchLocked && sellerEnabled !== initialSeller) { // untouched → keep following the server default
          updated = await updateUserSellerConfig(currentUser.username, currentUser.username, sellerEnabled);
        }
        if (pauseWithoutKite !== initialPauseWithoutKite) {
          updated = await updateUserFeedConfig(currentUser.username, currentUser.username, pauseWithoutKite);
        }
        const limitsChanged = DAILY_LIMITS.some(l => (currentUser?.[l.field] ?? null) !== limitsBody[l.key])
          || (currentUser?.limitProfitLockMode ?? null) !== limitsBody.profitLockMode;
        if (limitsChanged) {
          updated = await updateUserLimitsConfig(currentUser.username, currentUser.username, limitsBody);
        }
      }
      setSuccess('Saved.');
      onSaved?.(updated);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save risk settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rsp-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rsp-modal">
        <div className="rsp-header">
          <span>My Risk Settings</span>
          <button className="rsp-close" onClick={onClose}>✕</button>
        </div>

        <p className="rsp-note">
          {riskSizing
            ? <>SYSTEM trades are sized by risk (see <b>SYSTEM trade sizing</b> below), not by these
                lot counts. The counts prefill the manual trade tool and are remembered until you
                change them.</>
            : <>How many lots the SYSTEM engine trades on each tier (risk-based sizing is off), and
                the prefill for the manual trade tool. Applies to every symbol.</>}
        </p>

        <form onSubmit={handleSave}>
          {error   && <div className="rsp-error">{error}</div>}
          {success && <div className="rsp-success">{success}</div>}

          <div className="rsp-form-row">
            <div className="rsp-form-field">
              <label>ON RISK lots{riskSizing ? ' (manual tool)' : ''}</label>
              <input
                type="number" min="1" max="50" step="1"
                value={onRiskLots}
                onChange={e => setOnRiskLots(e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="rsp-form-field">
              <label>TAKE TRADE lots{riskSizing ? ' (manual tool)' : ''}</label>
              <input
                type="number" min="1" max="50" step="1"
                value={takeTradeLots}
                onChange={e => setTakeTradeLots(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          {isAdmin && (
            <>
              <div className="rsp-section-title">SYSTEM trade sizing</div>
              <div className="rsp-sizing">
                {sizingNow ? (
                  sizingNow.mode === 'RISK' ? (
                    <div>In use now: <b>{inr(sizingNow.capitalRs)}</b>{' '}
                      {sizingNow.capitalSource === 'ADMIN' ? '(your setting)' : '(server default, sizing.capital-rs)'}
                    </div>
                  ) : (
                    <div>In use now: <b>fixed lot counts</b> — no capital set and the server default is 0.</div>
                  )
                ) : (
                  <div>In use now: not known yet (the engine status hasn't loaded).</div>
                )}
                {formRiskSizing && formCapital > 0 && (
                  <ul>
                    {[['TAKE TRADE', takeTradeRiskPct], ['ON RISK', onRiskRiskPct]].map(([tier, pct]) => {
                      const p = parseFloat(pct);
                      return (
                        <li key={tier}>
                          {tier}: {p > 0 ? `${p}% of ${inr(formCapital)} = ${inr(formCapital * p / 100)}` : '—'} risk
                          per trade; lots = that ÷ (stop distance × lot size), rounded down
                        </li>
                      );
                    })}
                    <li>
                      Halved{sizingNow?.unsureDaySize != null && sizingNow.unsureDaySize < 1 ? ' on UNSURE days,' : ''} after
                      the day's profit goal, and when the index's recent win rate is weak (Wilson lower-90
                      under 50%, 15+ closed TAKE TRADE trades); at most {sizingNow?.maxLots ?? 20} lots; skipped
                      if even 1 lot risks over 1.5× the budget
                      {sizingNow?.profitLockHalfSize ? ' — the profit-goal halving is on today' : ''}
                    </li>
                  </ul>
                )}
              </div>
              <div className="rsp-form-row">
                <div className="rsp-form-field rsp-checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={capitalEnabled}
                      onChange={e => setCapitalEnabled(e.target.checked)}
                      disabled={saving}
                    />
                    {' '}Set my own capital{serverCapital != null ? ` (unticked: server default ${inr(serverCapital)})` : ''}
                  </label>
                </div>
                {capitalEnabled && (
                  <div className="rsp-form-field">
                    <label>Account capital (₹)</label>
                    <input
                      type="number" min="1" step="1000"
                      value={accountCapital}
                      onChange={e => setAccountCapital(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                )}
              </div>
              <div className="rsp-form-row">
                <div className="rsp-form-field">
                  <label>ON RISK risk % of capital</label>
                  <input
                    type="number" min="0.1" max="10" step="0.1"
                    value={onRiskRiskPct}
                    onChange={e => setOnRiskRiskPct(e.target.value)}
                    disabled={saving || !formRiskSizing}
                  />
                </div>
                <div className="rsp-form-field">
                  <label>TAKE TRADE risk % of capital</label>
                  <input
                    type="number" min="0.1" max="10" step="0.1"
                    value={takeTradeRiskPct}
                    onChange={e => setTakeTradeRiskPct(e.target.value)}
                    disabled={saving || !formRiskSizing}
                  />
                </div>
              </div>

              <p className="rsp-note">
                Range-day option selling (paper): when the engine flags a range day it sells one
                hedged structure per index — iron condor, iron fly, bull put or bear call spread,
                picked from the option chain. Turning this off stops new structures from the next
                poll; any open structure is still managed to its exit.
              </p>
              <div className="rsp-form-row">
                <div className="rsp-form-field rsp-checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={sellerEnabled}
                      onChange={e => setSellerEnabled(e.target.checked)}
                      disabled={saving || sellerSwitchLocked}
                    />
                    {' '}Sell premium on range days
                  </label>
                </div>
              </div>
              {sellerSwitchLocked && (
                <p className="rsp-note">
                  Locked by the server (seller.admin-switch=false): intraday selling is{' '}
                  {sellerEnabledNow ? 'on' : 'off'} by its seller.enabled setting. Premium is sold by
                  the positional condors instead (NIFTY weekly, BANKNIFTY monthly).
                </p>
              )}

              <p className="rsp-note">
                Trading without Kite: with a Kite login the engine runs on live ticks every 5 s.
                Without one it falls back to NSE REST every 30 s and, by default, keeps trading on
                it. Tick to pause new SYSTEM entries (buys and range-selling) whenever the data
                isn't from Kite; open positions are still managed. A Kite outage mid-session
                always pauses entries, whatever this is set to.
              </p>
              <div className="rsp-form-row">
                <div className="rsp-form-field rsp-checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={pauseWithoutKite}
                      onChange={e => setPauseWithoutKite(e.target.checked)}
                      disabled={saving}
                    />
                    {' '}Don't trade without a Kite login (NSE-only data)
                  </label>
                </div>
              </div>

              <p className="rsp-note">
                Daily limits (SYSTEM engine, every index): once one is hit, no new entries for the
                rest of the day; open positions are still managed. Raise them on a trending day
                after an early loss — a change applies from the next poll and also lifts a limit
                already hit today. 0 switches a limit off. A loss is a close at −0.25R or worse
                after costs, and range-selling losses count too. The profit lock works on the day's
                closed net as a % of capital: at the goal new entries go to half size, and (in the
                default mode) stop for the day once it gives back the set amount from its peak — never
                locking below +0.5%.
              </p>
              {DAILY_LIMITS.map(l => (
                <div className="rsp-form-row" key={l.key}>
                  <div className="rsp-form-field">
                    <label>{l.label}</label>
                    <input
                      type="number" min="0" max={l.int ? 50 : 20} step={l.int ? 1 : 0.5}
                      value={limits[l.key]}
                      onChange={e => setLimits(prev => ({ ...prev, [l.key]: e.target.value }))}
                      disabled={saving}
                    />
                    <span className="rsp-default-hint">
                      Server default {limitDefault(l)}{limitDefault(l) === 0 ? ' (off)' : ''}
                      {Number(limits[l.key]) !== limitDefault(l) ? ' · overridden' : ''}
                    </span>
                  </div>
                </div>
              ))}
              <div className="rsp-form-row">
                <div className="rsp-form-field">
                  <label>Profit lock mode</label>
                  <select value={profitLockMode} onChange={e => setProfitLockMode(e.target.value)} disabled={saving}>
                    {PROFIT_LOCK_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <span className="rsp-default-hint">
                    Server default {profitLockModeDefault}{profitLockMode !== profitLockModeDefault ? ' · overridden' : ''}
                  </span>
                </div>
              </div>
              <button
                type="button" className="rsp-link-btn"
                disabled={saving || limitsAtDefault}
                onClick={() => {
                  setLimits(Object.fromEntries(DAILY_LIMITS.map(l => [l.key, String(limitDefault(l))])));
                  setProfitLockMode(profitLockModeDefault);
                }}
              >
                Reset daily limits to server defaults
              </button>
            </>
          )}

          <button type="submit" className="rsp-save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RiskSettingsPanel;
